/**
 * Team Admin → Identity Providers (FR-AUTH-01, FR-AUTH-02, FR-AUTH-06).
 *
 * Three jobs on one screen:
 *   1. **Provider configuration** — add / edit / enable / delete a SAML 2.0 or
 *      SCIM 2.0 provider, with the SP endpoints a school pastes into its IdP
 *      (ACS URL, entity ID, metadata URL, SCIM base URL).
 *   2. **Allowed personas policy** — the FR-AUTH-02 checkbox grid, enforced at
 *      login by `services/auth/src/domain/provider-policy.ts`.
 *   3. **Federation status** — protocol badge, active state, realm state and
 *      last-used timestamp.
 *
 * Elite-only: the API answers 402 for every other plan and this page renders a
 * locked preview (upgrade CTA, reference endpoints, disabled table — see
 * `identity-providers-locked.tsx`) instead of a broken table or a blank page. Every mutation is audit-logged
 * server-side; destructive ones ask first. Search / filters / sort / page live
 * in the URL, so a refresh or a shared link restores the same view.
 */
import { useState } from 'react';

import { useApiClient } from '../../app/providers';
import { useToast } from '../../app/toast-context';
import { DataTable } from '../../components/data-table';
import { PageHeader } from '../../components/page-header';
import { ApiError } from '../../lib/api-error';
import type { Persona } from '../../lib/personas';

import { ServiceProviderDetails } from './federation-endpoints';
import type { ProviderFormValues } from './identity-provider-form';
import { LockedIdentityProviders } from './identity-providers-locked';
import {
  buildProviderColumns,
  providerMenuItems,
  type ProviderRowHandlers,
  PROTOCOL_FILTER_OPTIONS,
  STATUS_FILTER_OPTIONS,
} from './provider-columns';
import { ProviderDialogs, type OpenDialog } from './provider-dialogs';
import {
  IDENTITY_PROVIDERS_PATH,
  useIdentityProviders,
  type IdentityProviderView,
} from './use-identity-providers';

export function IdentityProvidersPage(): JSX.Element {
  const apiClient = useApiClient();
  const { showToast } = useToast();
  const { query, setQuery, providers, total, endpoints, isLoading, error, tierError, reload } =
    useIdentityProviders();
  const [dialog, setDialog] = useState<OpenDialog>({ kind: 'none' });
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [draftPersonas, setDraftPersonas] = useState<Persona[]>([]);

  if (tierError !== null) {
    // Locked preview: header, endpoints, disabled table and the upgrade path in
    // one view — never a lone banner or a blank workspace.
    return <LockedIdentityProviders tierError={tierError} />;
  }

  function closeDialog(): void {
    setDialog({ kind: 'none' });
    setDialogError(null);
  }

  /** Run a mutation, surfacing failures inside the open dialog. */
  async function guarded(action: () => Promise<void>, fallback: string): Promise<void> {
    setDialogError(null);
    try {
      await action();
    } catch (caught) {
      setDialogError(ApiError.isApiError(caught) ? caught.message : fallback);
    }
  }

  async function patchProvider(providerId: string, body: Record<string, unknown>): Promise<void> {
    await apiClient.request(`${IDENTITY_PROVIDERS_PATH}/${providerId}`, { method: 'PATCH', body });
  }

  async function savePolicy(providerId: string, personas: readonly Persona[]): Promise<void> {
    await apiClient.request(`${IDENTITY_PROVIDERS_PATH}/${providerId}/policy`, {
      method: 'PUT',
      body: { allowed_personas: personas },
    });
  }

  /** Create (and reveal the one-time SCIM token) or update + re-save policy. */
  async function submitProvider(values: ProviderFormValues, existingId?: string): Promise<void> {
    const shared = {
      display_name: values.display_name.trim(),
      is_enabled: values.is_enabled,
      saml_idp_entity_id: values.saml_idp_entity_id.trim() || undefined,
      saml_sso_url: values.saml_sso_url.trim() || undefined,
      saml_issuer_cert_pem: values.saml_issuer_cert_pem.trim() || undefined,
      scim_bearer_token_ref: values.scim_bearer_token_ref.trim() || undefined,
    };
    if (existingId === undefined) {
      const created = await apiClient.request<{
        data: IdentityProviderView;
        meta: { scim_bearer_token?: string };
      }>(IDENTITY_PROVIDERS_PATH, {
        method: 'POST',
        body: {
          ...shared,
          protocol: values.protocol,
          slug: values.slug.trim(),
          allowed_personas: values.allowed_personas,
          default_persona: values.default_persona === '' ? undefined : values.default_persona,
        },
      });
      reload();
      showToast(`${created.data.display_name} was added.`, 'success');
      const token = created.meta?.scim_bearer_token;
      if (token) setDialog({ kind: 'token', provider: created.data, token });
      else closeDialog();
      return;
    }
    await patchProvider(existingId, shared);
    await savePolicy(existingId, values.allowed_personas);
    closeDialog();
    showToast(`${values.display_name} was updated.`, 'success');
    reload();
  }

  const openCreate = (): void => setDialog({ kind: 'create' });
  const rowHandlers: ProviderRowHandlers = {
    onEditPersonas: (target) => {
      setDraftPersonas([...target.allowed_personas]);
      setDialogError(null);
      setDialog({ kind: 'policy', provider: target });
    },
    onRotate: (target) => {
      setDialogError(null);
      setDialog({ kind: 'rotate', provider: target });
    },
    onToggleEnabled: (target) => {
      setDialogError(null);
      if (target.is_enabled) {
        setDialog({ kind: 'disable', provider: target });
        return;
      }
      void guarded(async () => {
        await patchProvider(target.provider_id, { is_enabled: true });
        showToast(`${target.display_name} is active again.`, 'success');
        reload();
      }, 'The provider could not be enabled.');
    },
    onDelete: (target) => {
      setDialogError(null);
      setDialog({ kind: 'delete', provider: target });
    },
  };

  return (
    <section className="stack federation">
      <PageHeader
        title="Identity Providers"
        context={
          <>
            {total} {total === 1 ? 'provider' : 'providers'} · SAML 2.0 single sign-on and SCIM 2.0
            directory sync
          </>
        }
        actions={
          <button type="button" className="btn btn--primary" onClick={openCreate}>
            Add Provider
          </button>
        }
      />

      {endpoints === null ? null : (
        <ServiceProviderDetails
          endpoints={endpoints}
          note={
            endpoints.is_realm_provisioning_available ? null : (
              <>
                Realm provisioning is not configured on this deployment, so providers are stored but
                no Keycloak realm is created. Ask your administrator to set{' '}
                <code>IDP_ADMIN_CLIENT_ID</code>.
              </>
            )
          }
        />
      )}

      <DataTable<IdentityProviderView>
        caption="Configured identity providers"
        surface="identity-providers"
        layoutName="Identity provider columns"
        columns={buildProviderColumns()}
        rows={providers}
        getRowId={(provider) => provider.provider_id}
        getRowClassName={(provider) =>
          provider.is_enabled ? undefined : 'data-table__row--inactive'
        }
        sort={{ by: query.sortBy, order: query.sortOrder }}
        onSortChange={(sort) =>
          setQuery({ sortBy: sort.by as typeof query.sortBy, sortOrder: sort.order })
        }
        search={{
          value: query.search,
          onChange: (value) => setQuery({ search: value }),
          label: 'Search providers',
          placeholder: 'Search name or identifier',
        }}
        filters={[
          {
            id: 'protocol',
            label: 'Protocol',
            value: query.protocol,
            defaultValue: 'all',
            options: PROTOCOL_FILTER_OPTIONS,
            onChange: (value) => setQuery({ protocol: value as typeof query.protocol }),
          },
          {
            id: 'status',
            label: 'Status',
            value: query.status,
            defaultValue: 'all',
            options: STATUS_FILTER_OPTIONS,
            onChange: (value) => setQuery({ status: value as typeof query.status }),
          },
        ]}
        pagination={{
          page: query.page,
          pageSize: query.pageSize,
          total,
          onPageChange: (page) => setQuery({ page }),
          onPageSizeChange: (pageSize) => setQuery({ pageSize }),
        }}
        isLoading={isLoading}
        error={error}
        onRetry={reload}
        emptyTitle="No identity providers yet"
        emptyDescription="Connect your school's SSO so staff sign in with their district account, or let your directory provision users automatically."
        emptyAction={
          <button type="button" className="btn btn--primary btn--sm" onClick={openCreate}>
            Add your first provider
          </button>
        }
        actionsWidth={104}
        primaryRowAction={(provider) => (
          <button
            type="button"
            className="btn btn--secondary btn--sm"
            onClick={() => setDialog({ kind: 'edit', provider })}
          >
            Edit
          </button>
        )}
        rowMenu={(provider) => providerMenuItems(provider, rowHandlers)}
        getRowMenuLabel={(provider) => `More actions for ${provider.display_name}`}
      />

      <ProviderDialogs
        dialog={dialog}
        error={dialogError}
        draftPersonas={draftPersonas}
        onDraftPersonasChange={setDraftPersonas}
        onClose={closeDialog}
        onSubmitProvider={submitProvider}
        onSavePolicy={(provider) =>
          guarded(async () => {
            await savePolicy(provider.provider_id, draftPersonas);
            closeDialog();
            showToast(`Sign-in policy updated for ${provider.display_name}.`, 'success');
            reload();
          }, 'The policy could not be saved.')
        }
        onDisable={(provider) =>
          guarded(async () => {
            await patchProvider(provider.provider_id, { is_enabled: false });
            closeDialog();
            showToast(`${provider.display_name} is disabled.`, 'success');
            reload();
          }, 'The provider could not be disabled.')
        }
        onRotate={(provider) =>
          guarded(async () => {
            const rotated = await apiClient.request<{ meta: { scim_bearer_token: string } }>(
              `${IDENTITY_PROVIDERS_PATH}/${provider.provider_id}/scim-token`,
              { method: 'POST', body: {} }
            );
            setDialog({ kind: 'token', provider, token: rotated.meta.scim_bearer_token });
            reload();
          }, 'The token could not be rotated.')
        }
        onDelete={(provider) =>
          guarded(async () => {
            await apiClient.request(`${IDENTITY_PROVIDERS_PATH}/${provider.provider_id}`, {
              method: 'DELETE',
            });
            closeDialog();
            showToast(`${provider.display_name} was deleted.`, 'success');
            reload();
          }, 'The provider could not be deleted.')
        }
      />
    </section>
  );
}
